/**
 * Auto-update checker for Sentinel desktop app.
 * Polls for new releases, compares semver, emits event — does NOT auto-install.
 */

import { eventBus } from '@/core/events';
import { isTauriRuntime } from '@/utils/tauri';

export interface UpdateInfo {
  available: boolean;
  currentVersion: string;
  latestVersion: string;
  releaseUrl: string;
  releaseNotes?: string;
}

const STORAGE_KEY = 'sentinel-dismissed-update';
const CHECK_DELAY_MS = 5_000;
const CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 hours

let currentInfo: UpdateInfo | null = null;
let intervalId: ReturnType<typeof setInterval> | null = null;

/** Compare two semver strings. Returns positive if a > b, negative if a < b, 0 if equal. */
function semverCompare(a: string, b: string): number {
  const parse = (v: string) => v.replace(/^v/, '').split('.').map(Number);
  const [aMajor, aMinor, aPatch] = parse(a);
  const [bMajor, bMinor, bPatch] = parse(b);
  if (aMajor !== bMajor) return aMajor - bMajor;
  if (aMinor !== bMinor) return aMinor - bMinor;
  return aPatch - bPatch;
}

/** Get the current app version from the Vite-injected package version. */
function getCurrentVersion(): string {
  return __APP_VERSION__;
}

/** Get the configured update check URL. */
function getCheckUrl(): string {
  const envUrl = import.meta.env.VITE_UPDATE_CHECK_URL;
  if (envUrl) return envUrl;
  // Default: no default repo — update checking is disabled without explicit config
  return '';
}

/** Check if a specific version has been dismissed by the user. */
function isDismissed(version: string): boolean {
  try {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    return dismissed === version;
  } catch {
    return false;
  }
}

/** Check for a newer release. */
export async function checkForUpdate(): Promise<UpdateInfo> {
  const currentVersion = getCurrentVersion();
  const url = getCheckUrl();

  if (!url) {
    return { available: false, currentVersion, latestVersion: currentVersion, releaseUrl: '' };
  }

  try {
    const response = await fetch(url, {
      headers: { 'Accept': 'application/vnd.github.v3+json' },
    });

    if (!response.ok) {
      return { available: false, currentVersion, latestVersion: currentVersion, releaseUrl: '' };
    }

    const data = await response.json();
    const latestVersion = (data.tag_name as string) ?? currentVersion;
    const releaseUrl = (data.html_url as string) ?? '';
    const releaseNotes = (data.body as string) ?? undefined;
    const available = semverCompare(latestVersion, currentVersion) > 0;

    const info: UpdateInfo = { available, currentVersion, latestVersion, releaseUrl, releaseNotes };
    currentInfo = info;

    if (available && !isDismissed(latestVersion)) {
      eventBus.emit('update:available', {
        currentVersion,
        latestVersion,
        releaseUrl,
      });
    }

    return info;
  } catch {
    // Network errors are silent — update check is non-critical
    return { available: false, currentVersion, latestVersion: currentVersion, releaseUrl: '' };
  }
}

/** Dismiss a specific version so the badge hides until a newer release. */
export function dismissUpdate(version: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, version);
  } catch {
    // Storage unavailable — ignore
  }
  if (currentInfo && currentInfo.latestVersion === version) {
    currentInfo = { ...currentInfo, available: false };
  }
}

/** Get the last known update info, if any. */
export function getUpdateInfo(): UpdateInfo | null {
  return currentInfo;
}

/** Initialize the update checker — only runs in Tauri desktop mode. */
export function initUpdateChecker(): void {
  if (!isTauriRuntime()) return;

  // Initial check after a short delay
  setTimeout(() => {
    checkForUpdate();
  }, CHECK_DELAY_MS);

  // Repeat every 6 hours
  intervalId = setInterval(() => {
    checkForUpdate();
  }, CHECK_INTERVAL_MS);
}

/** Stop the update checker (for testing/cleanup). */
export function stopUpdateChecker(): void {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
}
